import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Trash, EnvelopeSimple, PaperPlaneTilt, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const ROLE_TONE = { admin: "red", buero: "blue", innendienst: "amber", monteur: "green" };
const ROLES = [
  { v: "monteur", l: "Monteur (Außendienst)" },
  { v: "innendienst", l: "Innendienst" },
  { v: "buero", l: "Büro" },
  { v: "admin", l: "Administrator" },
];

function DigestSettings() {
  const [cfg, setCfg] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/digest/settings").then((r) => setCfg(r.data)).catch(() => setCfg({ recipients: [], enabled: true })); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/digest/settings", { recipients: next.recipients, enabled: next.enabled });
      setCfg(data);
      toast.success("Digest-Einstellungen gespeichert");
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || cfg.recipients.includes(e)) return;
    save({ ...cfg, recipients: [...cfg.recipients, e] });
    setNewEmail("");
  };
  const removeEmail = (e) => save({ ...cfg, recipients: cfg.recipients.filter((x) => x !== e) });
  const toggle = (v) => save({ ...cfg, enabled: v });

  const sendTest = async () => {
    try {
      const { data } = await api.post("/digest/send-test");
      if (data.failed?.length) {
        toast.warning("Testversand teils fehlgeschlagen", {
          description: `Gesendet: ${data.sent.length}, Fehler: ${data.failed.length} (E-Mail-Konfiguration prüfen)`, duration: 12000,
        });
      } else {
        toast.success("Test-Digest versendet", { description: `An ${data.sent.length} Empfänger` });
      }
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!cfg) return <Skeleton className="h-40 rounded-md" />;

  return (
    <Card className="mt-8 rounded-md border p-6" data-testid="digest-settings">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted"><EnvelopeSimple size={20} weight="bold" /></div>
          <div>
            <div className="font-heading text-lg font-black">Wochen-Digest</div>
            <div className="text-xs text-muted-foreground">Montags 07:00 · gesperrte Anlagen, Sicherheitsmängel, überfällige Wartungen, neue Reparaturen</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{cfg.enabled ? "Aktiv" : "Pausiert"}</span>
          <Switch data-testid="digest-enabled" checked={cfg.enabled} onCheckedChange={toggle} disabled={saving} />
        </div>
      </div>

      <Label2>Empfänger</Label2>
      <div className="mt-2 flex flex-wrap gap-2">
        {cfg.recipients.length === 0 && <span className="text-sm text-muted-foreground">Noch keine Empfänger konfiguriert.</span>}
        {cfg.recipients.map((e) => (
          <span key={e} data-testid="digest-recipient" className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-sm">
            {e}
            <button onClick={() => removeEmail(e)} data-testid={`digest-remove-${e}`} className="text-muted-foreground hover:text-danger"><X size={14} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Input data-testid="digest-new-email" type="email" placeholder="empfaenger@firma.de" value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())} className="max-w-xs" />
        <Button variant="outline" data-testid="digest-add" onClick={addEmail} disabled={!newEmail.trim()} className="gap-1.5"><Plus size={16} weight="bold" />Hinzufügen</Button>
        <Button variant="secondary" data-testid="digest-send-test" onClick={sendTest} disabled={cfg.recipients.length === 0} className="ml-auto gap-1.5"><PaperPlaneTilt size={16} weight="bold" />Testversand</Button>
      </div>
    </Card>
  );
}

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "monteur" });

  const load = useCallback(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const { data } = await api.post("/admin/users", form);
      if (data.email_sent) {
        toast.success("Benutzer angelegt", { description: `Initiales Passwort per E-Mail an ${form.email} gesendet.` });
      } else {
        toast.warning("Benutzer angelegt – E-Mail nicht versendet", {
          description: `Initiales Passwort: ${data.initial_password} (bitte manuell weitergeben)`,
          duration: 15000,
        });
      }
      setOpen(false); setForm({ name: "", email: "", role: "monteur" }); load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Benutzer gelöscht"); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!users) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Verwaltung" subtitle="Benutzer, Rollen & Rechte (RBAC)">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-button" className="gap-2"><UserPlus size={18} weight="bold" />Benutzer</Button></DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader><DialogTitle className="font-heading">Benutzer anlegen</DialogTitle>
              <DialogDescription>Neuen Zugang anlegen – initiales Passwort wird per E-Mail versendet.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label2>Name *</Label2><Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>E-Mail *</Label2><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Rolle</Label2>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">Der Benutzer erhält ein initiales Passwort per E-Mail und muss beim ersten Login ein eigenes festlegen.</p>
            </div>
            <DialogFooter><Button data-testid="user-save" onClick={submit} disabled={!form.name || !form.email}>Anlegen & einladen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <Card className="divide-y divide-border rounded-md border">
        {users.map((u) => (
          <div key={u.id} data-testid="user-row" className="flex items-center justify-between gap-3 px-5 py-4">
            <div>
              <div className="font-semibold">{u.name}</div>
              <div className="text-xs text-muted-foreground">{u.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge tone={ROLE_TONE[u.role]}>{u.role}</StatusBadge>
              <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)}><Trash size={16} /></Button>
            </div>
          </div>
        ))}
      </Card>
      <DigestSettings />
    </div>
  );
}
